import { Label } from "@radix-ui/react-label";
import { useTranslation } from "react-i18next";

import { Badge } from "@/components/Badge";
import Icon from "@/components/Icon/Icon";
import { InputEmail } from "@/components/Input/Email/InputEmail";
import Input from "@/components/Input/Input";
import { InputPhoneNumber } from "@/components/Input/Phone/InputPhoneNumber";
import type { PhoneValue } from "@/components/Input/Phone/state.machine";
import { HStack, VStack } from "@/components/Layout";
import { Text } from "@/components/Typography";
import { Button } from "@/components/ui/button";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import {
  ContactPreference,
  ContactPreferenceOptions,
} from "@/constants/contactPreference";
import { useCommonTranslation } from "@/hooks/useCommonTranslation";
import { useAuthStore } from "@/lib/auth";
import { cn } from "@/lib/cn";
import { defaultNS } from "@/lib/i18n";
import type { ErrorObject } from "@/utils/error-object";

import {
  CFormItemBase,
  type CFormItemProps,
  buildCFormItem,
} from "../CFormItem";

export type Contact = {
  name: string | null;
  position: string | null;
  phone: PhoneValue | null;
  email: string | null;
  preference: ContactPreference | null;
};

type Props = Omit<CFormItemProps, "errors"> & {
  value: Contact;
  onChange: (value: Contact) => void;
  onRemove: () => void;
  errorObject?: ErrorObject<Contact>;
  inputWidth?: number | string;
  inputClassName?: string;
};

/**
 * Card-based Form / ContactItem
 */
export const CFormContactItem = buildCFormItem((props: Props) => {
  const { t } = useTranslation(defaultNS, {
    keyPrefix: "component.contract",
  });
  const tc = useCommonTranslation();
  const locationDialCode =
    useAuthStore.getState().getLocationDialCode() ?? "81";
  const defaultPhoneValue = {
    phoneNumber: "",
    dialCode: locationDialCode ?? "",
  };
  return (
    <CFormItemBase
      {...props}
      readComponent={
        <VStack className="gap-2">
          <VStack className="gap-1">
            <Text className="text-labelSmall text-neutral-muted-on-fill font-semibold">
              {props.value?.position}
            </Text>
            <Text className="text-labelLarge font-semibold">
              {props.value?.name}
            </Text>
          </VStack>
          <HStack className="gap-2 items-center">
            <Icon iconName="Mail" colorVariant="primary" />
            <Text className="font-semibold">{props.value?.email}</Text>
            {props.value?.preference === ContactPreference.EMAIL && (
              <Badge variant="primaryMuted">
                <Icon
                  iconName="StarIcon"
                  textColor="text-primary"
                  className="mr-1"
                />
                {t("preferred")}
              </Badge>
            )}
          </HStack>
          <HStack className="gap-2 items-center">
            <Icon iconName="Phone" colorVariant="primary" />
            <Text className="font-semibold">
              {props.value?.phone?.phoneNumber}
            </Text>
            {props.value?.preference === ContactPreference.PHONE && (
              <Badge variant="primaryMuted">
                <Icon
                  iconName="StarIcon"
                  textColor="text-primary"
                  className="mr-1"
                />
                {t("preferred")}
              </Badge>
            )}
          </HStack>
        </VStack>
      }
      writeComponent={
        <VStack className="gap-4">
          <HStack className="gap-2 items-center">
            <Text variant="label" className="text-neutral-muted-on-fill w-40">
              {t("name")}
            </Text>
            <Input
              value={props.value?.name ?? ""}
              onChange={(e) =>
                props.onChange({ ...props.value, name: e.target.value || null })
              }
              width={props.inputWidth}
              className={props.inputClassName}
              disabled={props.readonly}
            />
          </HStack>
          <HStack className="gap-2 items-center">
            <Text variant="label" className="text-neutral-muted-on-fill w-40">
              {t("position")}
            </Text>
            <Input
              value={props.value?.position ?? ""}
              onChange={(e) =>
                props.onChange({
                  ...props.value,
                  position: e.target.value || null,
                })
              }
              width={props.inputWidth}
              className={props.inputClassName}
              disabled={props.readonly}
            />
          </HStack>
          <HStack className="gap-2 items-center">
            <Text variant="label" className="text-neutral-muted-on-fill w-40">
              {t("phoneNumber")}
            </Text>
            <InputPhoneNumber
              value={props.value?.phone ?? defaultPhoneValue}
              onChange={(value) =>
                props.onChange({ ...props.value, phone: value })
              }
              required={props.required}
            />
          </HStack>
          <HStack className="gap-2 items-center">
            <Text variant="label" className="text-neutral-muted-on-fill w-40">
              {t("email")}
            </Text>
            <InputEmail
              value={props.value?.email ?? ""}
              onChange={(value) =>
                props.onChange({ ...props.value, email: value || null })
              }
            />
          </HStack>
          <HStack className="gap-2 items-start">
            <Text variant="label" className="text-neutral-muted-on-fill w-40">
              {t("contactPreference")}
            </Text>
            <RadioGroup
              value={props.value.preference ?? ""}
              onValueChange={(value) =>
                props.onChange({
                  ...props.value,
                  preference: value as ContactPreference,
                })
              }
              disabled={props.readonly}
              className={cn("flex", "flex-col gap-3")}
            >
              {ContactPreferenceOptions(tc).map((option) => (
                <div key={option.id} className="flex items-center space-x-2">
                  <RadioGroupItem
                    value={option.id}
                    id={`${props.id}-${option.id}`}
                    disabled={props.readonly}
                  />
                  <Label
                    htmlFor={`${props.id}-${option.id}`}
                    className={cn(
                      "text-sm font-normal cursor-pointer",
                      props.readonly &&
                        "text-muted-foreground cursor-not-allowed",
                    )}
                  >
                    {option.label}
                  </Label>
                </div>
              ))}
            </RadioGroup>
          </HStack>
          <HStack className="gap-2 items-end justify-end">
            <Button
              variant="ghost"
              className="p-2 w-8 h-8"
              onClick={props.onRemove}
            >
              <Icon iconName="Trash2Icon" />
            </Button>
          </HStack>
        </VStack>
      }
    />
  );
});
